/**
 * Conditions d'achat (registre B26, lot P1) — la logique de l'écran, sans navigateur.
 *
 * Le SERVEUR est l'autorité : il valide, calcule et arrondit. Ce module ne fait que lire ce que Benjamin
 * tape (« 50+10 », « 0,6 »), le montrer à la française, regrouper les conditions par ligne du tableau et
 * dire en français pourquoi un prix n'a pas pu être calculé. Les contrôles de saisie sont le MIROIR de
 * `controlerSaisie` de l'API (mêmes bornes) pour donner la réponse sans aller-retour.
 *
 * AUCUNE valeur par défaut : une condition absente est montrée comme telle, jamais complétée.
 */
import type {
  ConditionAchat,
  FamilleDuTarif,
  RaisonSansPrixAchat,
  ReferenceDuTarif,
  TypeConditionAchat,
} from '../api/client';

export const NB_REMISES_MAX = 3;
export const ECHELLE_REMISE = 4;
export const ECHELLE_COEFFICIENT = 4;
export const COEFFICIENT_MIN = 0.05;
export const COEFFICIENT_MAX = 10;
/** Au-dessus de ce pourcentage, une remise est acceptée mais signalée. */
export const REMISE_SUSPECTE = 90;

// ----------------------------------------------------------------------------------------- clés normalisées

/**
 * La même clé que le serveur (`normaliserCle`) : majuscules, accents retirés, tout ce qui n'est ni lettre ni
 * chiffre supprimé. `GM 49_Chap 1` et `GM 49-Chap 1` sont la même famille.
 */
export function normaliserCle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

// --------------------------------------------------------------------------------------------- lecture

export type LectureDecimale =
  | { readonly ok: true; readonly valeur: string }
  | { readonly ok: false; readonly erreur: string };

/**
 * Lit un décimal POSITIF écrit à la française ou à l'anglaise : « 27,5 » ou « 27.5 » → « 27.5 ». Rend la chaîne
 * exacte envoyée au serveur (jamais un nombre à virgule). Refuse tout le reste au lieu de le deviner.
 */
export function lireDecimalFr(texte: string, decimales: number): LectureDecimale {
  const brut = texte.trim();
  if (brut === '') return { ok: false, erreur: 'Valeur vide.' };
  if (brut.includes(',') && brut.includes('.')) {
    return { ok: false, erreur: `« ${brut} » : une seule virgule, sans séparateur de milliers.` };
  }
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(brut);
  if (!m) return { ok: false, erreur: `« ${brut} » n’est pas un nombre positif.` };
  const fraction = m[2] ?? '';
  if (fraction.length > decimales) {
    return { ok: false, erreur: `« ${brut} » : au plus ${decimales} chiffres après la virgule.` };
  }
  return { ok: true, valeur: fraction === '' ? (m[1] as string) : `${m[1]}.${fraction}` };
}

/** « 27.5 » → « 27,5 » (zéros de fin retirés). */
function virgule(valeur: string): string {
  return valeur.includes('.') ? valeur.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',') : valeur;
}

/** « 55 » → « 55 % ». */
export function formaterPourcentage(valeur: string): string {
  return `${virgule(valeur)} %`;
}

/** « 0.6000 » → « 0,6 ». */
export function formaterCoefficient(valeur: string): string {
  return virgule(valeur);
}

/** Ce que laisse un coefficient : 0,6 → « 40 % » du prix de vente en marge. `null` à partir de 1 (aucune marge). */
/**
 * Ce que désigne la référence CHOISIE dans la liste du tarif (« SIGMA FLUID MOL 10 l — famille GM 52 _ Fluides »), à
 * montrer sous la case de l'exception : une confusion entre deux références voisines (10020 pour 10040) se voit
 * AVANT d'enregistrer. `null` tant que rien n'a été choisi, ou dès que la case ne porte plus la référence choisie
 * (la phrase ne parlerait plus de ce qui est tapé).
 */
export function phraseReferenceChoisie(choisie: ReferenceDuTarif | null, saisie: string): string | null {
  if (!choisie) return null;
  if (choisie.reference.trim().toLowerCase() !== saisie.trim().toLowerCase()) return null;
  const famille = choisie.famille?.trim();
  return famille ? `${choisie.description} — famille ${famille}` : choisie.description;
}

export function margeDuCoefficient(coefficient: string): string | null {
  const c = Number(coefficient);
  // Hors des bornes que le serveur accepte, aucune marge n'est annoncée (« 99 % » pour 0,01 serait trompeur).
  if (!Number.isFinite(c) || c < COEFFICIENT_MIN || c >= 1) return null;
  return `${virgule((Math.round((1 - c) * 100 * 10000) / 10000).toString())} %`;
}

/**
 * La remise pondérée d'une cascade, POUR L'AFFICHAGE (50 % puis 10 % → 55). Le serveur la recalcule en fractions
 * exactes ; ici un arrondi à 4 décimales suffit.
 */
export function remiseTotale(remises: readonly string[]): string {
  const facteur = remises.reduce((f, r) => f * (1 - Number(r) / 100), 1);
  return (Math.round((1 - facteur) * 100 * 10000) / 10000).toString();
}

export type LectureCascade =
  | { readonly ok: true; readonly remises: readonly string[]; readonly total: string }
  | { readonly ok: false; readonly erreur: string };

/**
 * Lit une cascade de remises : « 25 », « 50+10 », « 50 % + 10 % », « 50 puis 10 ». Au plus 3 remises, chacune de
 * [0 ; 100[ pour cent. Vide = erreur (une remise est obligatoire pour le type « Remise »).
 */
export function lireCascade(texte: string): LectureCascade {
  const morceaux = texte
    .replace(/%/g, ' ')
    .split(/\+|\bpuis\b|\bet\b|;/i)
    .map((m) => m.trim())
    .filter((m) => m !== '');
  if (morceaux.length === 0) return { ok: false, erreur: 'Indique au moins une remise, par exemple 25 ou 50+10.' };
  if (morceaux.length > NB_REMISES_MAX) {
    return { ok: false, erreur: `Au plus ${NB_REMISES_MAX} remises en cascade.` };
  }
  const remises: string[] = [];
  for (const morceau of morceaux) {
    const lue = lireDecimalFr(morceau, ECHELLE_REMISE);
    if (!lue.ok) return { ok: false, erreur: lue.erreur };
    if (Number(lue.valeur) >= 100) return { ok: false, erreur: 'Une remise doit être inférieure à 100 %.' };
    remises.push(lue.valeur);
  }
  return { ok: true, remises, total: remiseTotale(remises) };
}

/** « 50 % puis 10 % = 55 % » pour une cascade, « 25 % » pour une remise seule. */
export function libelleRemises(remises: readonly string[]): string {
  if (remises.length === 0) return '';
  if (remises.length === 1) return formaterPourcentage(remises[0] as string);
  return `${remises.map(formaterPourcentage).join(' puis ')} = ${formaterPourcentage(remiseTotale(remises))}`;
}

/** Ce qu'une condition dit de l'achat : « Remise 25 % », « Remises 50 % puis 10 % = 55 % », « Prix net ». */
export function libelleAchat(c: Pick<ConditionAchat, 'kind' | 'remises'>): string {
  if (c.kind === 'NET') return 'Prix net';
  return `${c.remises.length > 1 ? 'Remises' : 'Remise'} ${libelleRemises(c.remises)}`;
}

// ------------------------------------------------------------------------------------ contrôle de saisie

export interface ControleSaisie {
  readonly erreurs: readonly string[];
  readonly avertissements: readonly string[];
}

/** Miroir de `controlerSaisie` de l'API : mêmes bornes, mêmes avertissements. */
export function controlerSaisie(entree: {
  readonly kind: TypeConditionAchat;
  /** Le texte de la cascade tel que saisi (ignoré pour un prix net). */
  readonly remises: string;
  readonly coefficient: string;
}): ControleSaisie & { readonly remisesLues: readonly string[] | null; readonly coefficientLu: string | null } {
  const erreurs: string[] = [];
  const avertissements: string[] = [];
  let remisesLues: readonly string[] | null = null;
  let coefficientLu: string | null = null;

  if (entree.kind === 'DISCOUNT') {
    const cascade = lireCascade(entree.remises);
    if (!cascade.ok) erreurs.push(cascade.erreur);
    else {
      remisesLues = cascade.remises;
      if (cascade.remises.some((r) => Number(r) > REMISE_SUSPECTE)) {
        avertissements.push(`Une remise de plus de ${REMISE_SUSPECTE} % est inhabituelle : vérifie la saisie.`);
      }
    }
  } else {
    remisesLues = [];
  }

  const lu = lireDecimalFr(entree.coefficient, ECHELLE_COEFFICIENT);
  if (!lu.ok) erreurs.push(`Coefficient : ${lu.erreur}`);
  else {
    const valeur = Number(lu.valeur);
    if (valeur < COEFFICIENT_MIN || valeur > COEFFICIENT_MAX) {
      erreurs.push(`Le coefficient doit être compris entre ${virgule(String(COEFFICIENT_MIN))} et ${COEFFICIENT_MAX}.`);
    } else {
      coefficientLu = lu.valeur;
      if (valeur >= 1) {
        avertissements.push('Un coefficient de 1 ou plus donne un prix de vente inférieur ou égal au prix d’achat.');
      }
    }
  }

  return { erreurs, avertissements, remisesLues, coefficientLu };
}

// -------------------------------------------------------------------------------- lignes du tableau

export interface LigneConditions {
  /** Identifiant stable de la ligne du tableau. */
  readonly cle: string;
  readonly portee: 'FOURNISSEUR' | 'FAMILLE' | 'REFERENCE';
  readonly titre: string;
  /** Nombre de références de la famille dans le tarif (`null` hors famille). */
  readonly references: number | null;
  /** Autres graphies de la même famille dans le tarif (à signaler). */
  readonly graphies: readonly string[];
  /** Une condition de famille dont aucune famille du tarif en vigueur ne porte la clé. */
  readonly sansCorrespondance: boolean;
  /** La condition qui s'applique AUJOURD'HUI, ou `null` : « aucune condition ». */
  readonly enVigueur: ConditionAchat | null;
  /** La prochaine, datée du futur (non archivée), ou `null`. */
  readonly aVenir: ConditionAchat | null;
  /** Toutes les lignes de cette portée, la plus récente d'abord. */
  readonly historique: readonly ConditionAchat[];
}

const plusRecentD = (a: ConditionAchat, b: ConditionAchat): number =>
  b.validFrom.localeCompare(a.validFrom) || b.createdAt.localeCompare(a.createdAt);

function ligne(
  base: Pick<LigneConditions, 'cle' | 'portee' | 'titre' | 'references' | 'graphies' | 'sansCorrespondance'>,
  conditions: readonly ConditionAchat[],
): LigneConditions {
  const historique = [...conditions].sort(plusRecentD);
  const aVenir = historique.find((c) => c.aVenir && c.archivedAt === null) ?? null;
  return { ...base, enVigueur: historique.find((c) => c.enVigueur) ?? null, aVenir, historique };
}

/**
 * Les lignes du tableau d'un fournisseur : « Tout le fournisseur », chaque famille de son tarif (avec ses
 * références), les familles conditionnées qui n'existent plus dans le tarif, puis les exceptions par référence.
 */
export function construireLignes(
  conditions: readonly ConditionAchat[],
  familles: readonly FamilleDuTarif[],
): LigneConditions[] {
  const lignes: LigneConditions[] = [];

  lignes.push(
    ligne(
      { cle: '*', portee: 'FOURNISSEUR', titre: 'Tout le fournisseur', references: null, graphies: [], sansCorrespondance: false },
      conditions.filter((c) => c.portee === 'FOURNISSEUR'),
    ),
  );

  const parFamille = new Map<string, ConditionAchat[]>();
  for (const c of conditions) {
    if (c.portee !== 'FAMILLE' || c.famille === null) continue;
    const cle = normaliserCle(c.famille);
    parFamille.set(cle, [...(parFamille.get(cle) ?? []), c]);
  }

  const clesDuTarif = new Set<string>();
  for (const f of familles) {
    clesDuTarif.add(f.cle);
    lignes.push(
      ligne(
        {
          cle: `F:${f.cle}`,
          portee: 'FAMILLE',
          titre: f.famille,
          references: f.references,
          graphies: f.graphies.length > 1 ? f.graphies : [],
          sansCorrespondance: false,
        },
        parFamille.get(f.cle) ?? [],
      ),
    );
  }

  for (const [cle, liste] of parFamille) {
    if (clesDuTarif.has(cle)) continue;
    const titre = [...liste].sort(plusRecentD)[0]?.famille ?? cle;
    lignes.push(
      ligne({ cle: `F:${cle}`, portee: 'FAMILLE', titre, references: null, graphies: [], sansCorrespondance: true }, liste),
    );
  }

  const parReference = new Map<string, ConditionAchat[]>();
  for (const c of conditions) {
    if (c.portee !== 'REFERENCE' || c.reference === null) continue;
    const cle = normaliserCle(c.reference);
    parReference.set(cle, [...(parReference.get(cle) ?? []), c]);
  }
  for (const [cle, liste] of parReference) {
    const titre = [...liste].sort(plusRecentD)[0]?.reference ?? cle;
    lignes.push(
      ligne({ cle: `R:${cle}`, portee: 'REFERENCE', titre, references: null, graphies: [], sansCorrespondance: false }, liste),
    );
  }

  return lignes;
}

// ------------------------------------------------------------------------------------------- phrases

const RAISONS: Record<RaisonSansPrixAchat, string> = {
  AUCUNE_CONDITION: 'Aucune condition d’achat ne s’applique : définis-la.',
  FAMILLE_INCONNUE:
    'La famille de cette référence est inconnue (elle n’est dans aucun tarif en vigueur) et ce fournisseur a des ' +
    'conditions par famille : crée une exception pour cette référence.',
  REFERENCE_AMBIGUE:
    'Cette référence est présente plusieurs fois dans les tarifs, avec des familles ou des prix différents : on ne ' +
    'choisit pas à ta place. Crée une exception pour cette référence.',
  PRIX_TARIF_INUTILISABLE: 'Le prix du tarif est nul ou inutilisable : aucun prix n’est calculé.',
  ACHAT_NUL: 'Le prix d’achat arrondi serait de 0,00 € : aucun prix n’est calculé.',
  MONTANT_INVRAISEMBLABLE: 'Le montant calculé dépasse 10 000 000 € : aucun prix n’est calculé.',
  AUCUN_PRIX_DE_TARIF: 'Cette référence n’a pas de prix dans un tarif : saisis un prix du tarif pour l’essayer.',
};

/** Pourquoi il n'y a pas de prix, en français. */
export function phraseRaison(raison: RaisonSansPrixAchat): string {
  return RAISONS[raison];
}

export const RAISONS_SANS_PRIX: readonly RaisonSansPrixAchat[] = Object.keys(RAISONS) as RaisonSansPrixAchat[];

/** Les rôles d'un tiers qui en font un fournisseur de pièces : on y pose des conditions d'achat. */
export const ROLES_FOURNISSEUR = ['BRAND', 'SUPPLIER', 'MANUFACTURER'] as const;

// ------------------------------------------------------------------ avec qui l'on travaille

/** Combien de conditions d'achat en cours (non archivées) un fournisseur porte : ce que rend `GET /pricing/fournisseurs`. */
export interface FournisseurAvecConditions {
  readonly partyId: string;
  readonly nbConditions: number;
}

interface TiersNomme {
  readonly id: string;
  readonly legalName: string;
}

const parNom = (a: TiersNomme, b: TiersNomme): number => a.legalName.localeCompare(b.legalName, 'fr');

/** Sans accents ni majuscules : « eferest » retrouve « EFEREST », « é » retrouve « e ». */
export function plierTexte(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Les tiers à LISTER sur l'écran des conditions d'achat : ceux avec qui l'on travaille réellement.
 *
 * Avoir une machine d'une marque dans le parc ne veut pas dire qu'on achète chez elle (réservoirs, séparateurs, marques
 * concurrentes qui refusent de vendre des pièces…) : lister toutes les marques noyait les vrais fournisseurs. Un tiers y
 * figure s'il a un TARIF non archivé, ou une CONDITION d'achat en cours, ou si on vient de l'AJOUTER (il le restera
 * ensuite grâce à la condition qu'on lui pose), ou si c'est celui qu'une adresse désigne. Les autres sont masqués, jamais
 * perdus : « Ajouter un fournisseur » les retrouve.
 */
export function fournisseursListes<T extends TiersNomme>(
  tiers: readonly T[],
  tarifs: readonly { readonly partyId: string; readonly archivedAt: string | null }[],
  comptes: readonly FournisseurAvecConditions[],
  ajoutes: ReadonlySet<string>,
  selectionne: string | null,
): T[] {
  const actifs = new Set<string>();
  for (const t of tarifs) if (!t.archivedAt) actifs.add(t.partyId);
  for (const c of comptes) if (c.nbConditions > 0) actifs.add(c.partyId);
  for (const id of ajoutes) actifs.add(id);
  if (selectionne) actifs.add(selectionne);
  return tiers.filter((t) => actifs.has(t.id)).sort(parNom);
}

/** Les tiers marque, fabricant ou fournisseur qui ne sont PAS listés : ceux qu'on peut ajouter. */
export function tiersMasques<T extends TiersNomme>(tiers: readonly T[], listes: readonly T[]): T[] {
  const ids = new Set(listes.map((t) => t.id));
  return tiers.filter((t) => !ids.has(t.id)).sort(parNom);
}

/** Les tiers masqués qui répondent à la recherche (accents et majuscules ignorés), les premiers seulement. */
export function candidatsAjout<T extends TiersNomme>(
  masques: readonly T[],
  recherche: string,
  max = 8,
): { readonly items: T[]; readonly total: number } {
  const voulu = plierTexte(recherche.trim());
  const trouves = masques.filter((t) => plierTexte(t.legalName).includes(voulu));
  return { items: trouves.slice(0, max), total: trouves.length };
}
